import { ReportDefinition } from '../models/report-definition';
import { ReportRowVm } from '../models/report-row-vm';
import { asNumberOrNull, asString } from '../utils/report-formatting.utils';
import { parseReportJson } from '../utils/report-json.utils';
import { getRealTyallsRowId } from '../utils/report-row-id.utils';

type HousekeepingVarianceRow = {
   rowId: string;
   floor: string;
   floorRaw: number | null;
   roomNumber: string;
   roomNumberRaw: number | null;
   roomType: string;
   roomStatus: string;
   discrepancy: string;
   discrepancyRaw: string;
   frontOfficeStatus: string;
   frontOfficeGuests: string;
   frontOfficeGuestsRaw: number | null;
   housekeepingStatus: string;
   housekeepingGuests: string;
   housekeepingGuestsRaw: number | null;
   highGuestCount: string;
   highGuestCountRaw: boolean;
   hasRealTyallsRowId: boolean;
};

function getRowId(row: any): string {
   const realTyallsRowId = getRealTyallsRowId(row);

   if (realTyallsRowId) {
      return realTyallsRowId;
   }

   return (
      asString(row?.Id ?? row?.id) ||
      `housekeeping-variance:${asString(row?.Floor)}:${asString(
         row?.RoomNumber
      )}:${asString(row?.Discrepancy)}`
   );
}

function humaniseCamelCase(value: string): string {
   if (!value) {
      return '';
   }

   return value
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/[_-]+/g, ' ')
      .trim();
}

function formatDiscrepancy(value: unknown): string {
   return humaniseCamelCase(asString(value));
}

function formatBoolean(value: unknown): string {
   if (value === true) {
      return 'Yes';
   }

   if (value === false) {
      return 'No';
   }

   return asString(value);
}

function getSourceRows(reportJson: unknown): any[] {
   const parsedReportJson = parseReportJson(reportJson);

   const housekeepingVarianceRows = parsedReportJson['HousekeepingVariance'];

   if (Array.isArray(housekeepingVarianceRows)) {
      return housekeepingVarianceRows;
   }

   const upperCaseRows = parsedReportJson['Rows'];

   if (Array.isArray(upperCaseRows)) {
      return upperCaseRows;
   }

   const lowerCaseRows = parsedReportJson['rows'];

   if (Array.isArray(lowerCaseRows)) {
      return lowerCaseRows;
   }

   return [];
}

function normaliseRows(reportJson: unknown): HousekeepingVarianceRow[] {
   return getSourceRows(reportJson)
      .map((row) => {
         const floorRaw = asNumberOrNull(row?.Floor);
         const roomNumberRaw = asNumberOrNull(row?.RoomNumber);
         const frontOfficeGuestsRaw = asNumberOrNull(row?.FrontOfficeGuests);
         const housekeepingGuestsRaw = asNumberOrNull(row?.HousekeepingGuests);
         const highGuestCountRaw = row?.HighGuestCount === true;

         return {
            rowId: getRowId(row),
            floor: asString(row?.Floor),
            floorRaw,
            roomNumber: asString(row?.RoomNumber),
            roomNumberRaw,
            roomType: asString(row?.RoomType),
            roomStatus: asString(row?.RoomStatus),
            discrepancy: formatDiscrepancy(row?.Discrepancy),
            discrepancyRaw: asString(row?.Discrepancy),
            frontOfficeStatus: asString(row?.FrontOfficeStatus),
            frontOfficeGuests: asString(row?.FrontOfficeGuests),
            frontOfficeGuestsRaw,
            housekeepingStatus: asString(row?.HousekeepingStatus),
            housekeepingGuests: asString(row?.HousekeepingGuests),
            housekeepingGuestsRaw,
            highGuestCount: formatBoolean(highGuestCountRaw),
            highGuestCountRaw,
            hasRealTyallsRowId: !!getRealTyallsRowId(row)
         };
      })
      .filter((row) => row.roomNumber || row.discrepancy);
}

function sortRows(rows: HousekeepingVarianceRow[]): HousekeepingVarianceRow[] {
   return [...rows].sort((a, b) => {
      if (a.floorRaw !== null && b.floorRaw !== null && a.floorRaw !== b.floorRaw) {
         return a.floorRaw - b.floorRaw;
      }

      if (
         a.roomNumberRaw !== null &&
         b.roomNumberRaw !== null &&
         a.roomNumberRaw !== b.roomNumberRaw
      ) {
         return a.roomNumberRaw - b.roomNumberRaw;
      }

      return a.roomNumber.localeCompare(b.roomNumber);
   });
}

export const HOUSEKEEPING_VARIANCE_DEFINITION: ReportDefinition = {
   reportType: 'HousekeepingVariance',
   version: 3,
   title: 'Housekeeping Variance',

   sorting: {
      enabled: true,
      persist: true
   },

   header: {
      title: 'Housekeeping Variance',
      riskCountSource: 'affectedRows'
   },

   rowChecks: {
      enabled: false,
      includeNestedRows: false,
      selectableRowTypes: ['housekeepingVariance']
   },

   annotations: {
      enabled: true,
      selectableRowTypes: ['housekeepingVariance'],
      disabledMessage:
         'Row comments and row highlighting are not available for this report layout. Please use the main report comments panel to add comments for this report.'
   },

   columns: [
      {
         key: 'floor',
         header: 'FLOOR',
         width: '90px',
         sort: {
            type: 'number',
            getValue: (row) => row.cells['floorRaw']
         }
      },
      {
         key: 'roomNumber',
         header: 'ROOM',
         width: '100px',
         sort: {
            type: 'number',
            getValue: (row) => row.cells['roomNumberRaw']
         }
      },
      {
         key: 'roomType',
         header: 'ROOM TYPE',
         width: '120px',
         sort: { type: 'string' }
      },
      {
         key: 'roomStatus',
         header: 'ROOM STATUS',
         width: '130px',
         sort: { type: 'string' }
      },
      {
         key: 'discrepancy',
         header: 'DISCREPANCY',
         width: '170px',
         sort: {
            type: 'string',
            getValue: (row) => row.cells['discrepancyRaw']
         }
      },
      {
         key: 'frontOfficeStatus',
         header: 'FO STATUS',
         width: '130px',
         sort: { type: 'string' }
      },
      {
         key: 'frontOfficeGuests',
         header: 'FO GUESTS',
         width: '120px',
         sort: {
            type: 'number',
            getValue: (row) => row.cells['frontOfficeGuestsRaw'],
            defaultDirection: 'desc'
         }
      },
      {
         key: 'housekeepingStatus',
         header: 'HK STATUS',
         width: '130px',
         sort: { type: 'string' }
      },
      {
         key: 'housekeepingGuests',
         header: 'HK GUESTS',
         width: '120px',
         sort: {
            type: 'number',
            getValue: (row) => row.cells['housekeepingGuestsRaw'],
            defaultDirection: 'desc'
         }
      },
      {
         key: 'highGuestCount',
         header: 'HIGH GUEST COUNT',
         width: '160px',
         sort: {
            type: 'custom',
            customOrder: ['Yes', 'No']
         }
      }
   ],

   filter: {
      fields: [
         {
            key: 'floor',
            label: 'Floor',
            type: 'number',
            getValue: (row) => row.cells['floorRaw']
         },
         {
            key: 'roomNumber',
            label: 'Room',
            type: 'number',
            getValue: (row) => row.cells['roomNumberRaw']
         },
         { key: 'roomType', label: 'Room Type', type: 'string' },
         { key: 'roomStatus', label: 'Room Status', type: 'string' },
         {
            key: 'discrepancy',
            label: 'Discrepancy',
            type: 'string',
            getValue: (row) => row.cells['discrepancyRaw']
         },
         {
            key: 'frontOfficeStatus',
            label: 'Front Office Status',
            type: 'string'
         },
         {
            key: 'frontOfficeGuests',
            label: 'Front Office Guests',
            type: 'number',
            getValue: (row) => row.cells['frontOfficeGuestsRaw']
         },
         {
            key: 'housekeepingStatus',
            label: 'Housekeeping Status',
            type: 'string'
         },
         {
            key: 'housekeepingGuests',
            label: 'Housekeeping Guests',
            type: 'number',
            getValue: (row) => row.cells['housekeepingGuestsRaw']
         },
         {
            key: 'highGuestCount',
            label: 'High Guest Count',
            type: 'string',
            getValue: (row) => row.cells['highGuestCount']
         }
      ]
   },

   flattenRows: (reportJson: unknown): ReportRowVm[] => {
      const rows = sortRows(normaliseRows(reportJson));

      return rows.map((row) => ({
         rowId: row.rowId,
         isAnnotatable: row.hasRealTyallsRowId,
         cells: {
            rowType: 'housekeepingVariance',
            floor: row.floor,
            floorRaw: row.floorRaw,
            roomNumber: row.roomNumber,
            roomNumberRaw: row.roomNumberRaw,
            roomType: row.roomType,
            roomStatus: row.roomStatus,
            discrepancy: row.discrepancy,
            discrepancyRaw: row.discrepancyRaw,
            frontOfficeStatus: row.frontOfficeStatus,
            frontOfficeGuests: row.frontOfficeGuests,
            frontOfficeGuestsRaw: row.frontOfficeGuestsRaw,
            housekeepingStatus: row.housekeepingStatus,
            housekeepingGuests: row.housekeepingGuests,
            housekeepingGuestsRaw: row.housekeepingGuestsRaw,
            highGuestCount: row.highGuestCount,
            highGuestCountRaw: row.highGuestCountRaw
         }
      }));
   }
};
